import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Trophy, Gamepad2, Ticket, ShoppingBag } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Poll } from "@/types/Poll";
import { ScrollArea } from "@/components/ui/scroll-area";
import BenefitBadge from "@/components/BenefitBadge";
import {useAuth} from "@/contexts/AuthContext.tsx";

const BenefitsCard = ({pollDetail}) => {
  const { slug } = useParams();
  /*const [userSession] = React.useState(() => {
    const cachedSession = localStorage.getItem('session');
    return cachedSession ? JSON.parse(cachedSession) : null;
  });*/
  const {user, session, loading} = useAuth();
  const [currentBadgeText, setCurrentBadgeText] = useState("");

  const badgeImages = {
    "Master Badge": "/lovable-uploads/4edcef8f-2806-4485-b6a5-50fb529d23b7.png",
    "New Year Prophet": "/lovable-uploads/7428a749-e4e2-48ca-b61b-6bff5441d16f.png",
    "Novice Badge": "/lovable-uploads/039533f8-87cd-4f92-afdc-e9317552a86f.png",
    "Night Owl": "/lovable-uploads/f4162cbb-7a47-415a-a397-cf0817c5d0be.png",
    "Oracle": "/lovable-uploads/93d2f389-e867-44d0-84d9-70fffcbbda61.png",
    "Poll Master": "/lovable-uploads/6e729466-fae7-4e67-a93a-a437d7abca2c.png",
    "Sharp Mind": "/lovable-uploads/7a779c9b-9f1a-4abb-bb18-df8e7b8c5ce0.png",
    "Summer Badge": "/lovable-uploads/e191f8b5-fa76-44d4-855b-3edc8fffe748.png",
    "Supreme Sage Badge": "/lovable-uploads/2a10bc90-a5e7-4d1e-9547-23b889943fb6.png",
    "Unlucky Hero": "/lovable-uploads/0b1515d5-7bef-4ab1-af80-997c8268d0be.png",
    "Valentine's Badge": "/lovable-uploads/3c0f768d-1259-4805-9081-f2a4c427a908.png",
    "Winter Prophet": "/lovable-uploads/fc5d04a0-bd75-4260-be6d-69db9dbfce98.png",
    "Annual Legend": "/lovable-uploads/898a531b-96b1-4947-82ed-69b0191a87fa.png",
    "Summer Sun": "/lovable-uploads/e191f8b5-fa76-44d4-855b-3edc8fffe748.png"
  };

  const badgeTexts = [
    "Is this your first poll? First Step badge awaits you.",
    "Completed 5 polls? You've earned the Novice Badge.",
    "Don't worry, even with 5 wrong polls, your Unlucky Hero badge is ready.",
    "Are you a night predictor? Night Owl is perfect for you.",
    "Participated in over 50 polls? You deserve the Master Badge.",
    "Did you get 50 polls right? That's incredible. You're an Oracle now.",
    "So you got 5 polls in a row? Great. Sharp Mind is with you.",
    "You're a true Chaos Creator. You're one of the best.",
    "Oh my god! 100 polls? You're a Marathon Runner.",
    "10 correct predictions. We can call you Poll Master now.",
    "Get ready for winter predictions! Winter Prophet badge awaits.",
    "Celebrate love with our Valentine's Badge.",
    "Summer is here! Get your Summer Badge ready.",
    "Become the Supreme Sage of predictions.",
    "Ring in the New Year as a Prophet!"
  ];

  useEffect(() => {
    if (!session) {
      setCurrentBadgeText(badgeTexts[Math.floor(Math.random() * badgeTexts.length)]);

      const interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * badgeTexts.length);
        setCurrentBadgeText(badgeTexts[randomIndex]);
      }, 3000 + Math.random() * 1000);

      return () => clearInterval(interval);
    }
  }, [session]);

  /*const { data: pollDetail } = useQuery({
    queryKey: ["polls", slug],
    queryFn: async () => {
      const url = `${import.meta.env.VITE_API_URL}/polls/${slug}`;
      const response = await fetch(url, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      return data;
    },
    enabled: !!slug,
  });*/

  const getActiveBenefits = () => {
    if (!pollDetail?.poll?.categories?.slug) return { badges: true };

    const category = pollDetail.poll.categories.slug;

    if (["trending", "gaming", "fun"].includes(category)) {
      return {
        badges: true,
        gameRewards: true,
        promoCodes: true,
        personalizedProducts: false,
      };
    }

    else if (["politics", "news"].includes(category)) {
      return {
        badges: true,
        gameRewards: false,
        promoCodes: false,
        personalizedProducts: false,
      };
    }

    else if (["finance", "sports", "technology"].includes(category)) {
      return {
        badges: true,
        gameRewards: false,
        promoCodes: true,
        personalizedProducts: true,
      };
    }

    return {
      badges: true,
      gameRewards: false,
      promoCodes: false,
      personalizedProducts: false,
    };
  };

  const activeBenefits = getActiveBenefits();

  if (!session) {
    return (
      <Card
        className="p-6 mb-4 shadow-sm overflow-hidden"
        style={{
          backgroundColor: "#FFF7DF",
          border: "1px dashed #ccc"
        }}
      >
        <h3 className="text-xl font-semibold mb-4" style={{ color: "#6F3B01" }}>
          Benefits
        </h3>
        <p className="mb-4 text-sm dark:text-white" style={{ color: "#6F3B01" }}>
          Vote correctly and win surprise rewards!
        </p>
        <div className="flex justify-around mb-5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Trophy className="h-5 w-5" style={{ color: "#6F3B01" }} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Badges</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Gamepad2 className="h-5 w-5" style={{ color: "#6F3B01" }} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>In-game rewards</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Ticket className="h-5 w-5" style={{ color: "#6F3B01" }} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Promo codes & discounts</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <ShoppingBag className="h-5 w-5" style={{ color: "#6F3B01" }} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Personalized products & more!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {currentBadgeText && (
          <div
            className="p-2 rounded-full mt-2 text-sm dark:text-white"
            style={{
              color: "#6F3B01",
              backgroundColor: "#FCF9EE",
              opacity: "0.9",
              animation: "slideInUp 0.5s ease-in-out",
              overflow: "hidden",
              border: "1px dashed #ccc",
              borderRadius: "9999px",
              textAlign: "center",
            }}
          >
            <style>
              {`
                @keyframes slideInUp {
                  from {
                    transform: translateY(20px);
                    opacity: 0;
                  }
                  to {
                    transform: translateY(0);
                    opacity: 0.9;
                  }
                }
              `}
            </style>
            {currentBadgeText}
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card
      className="p-6 mb-4 shadow-sm overflow-hidden"
      style={{
        backgroundColor: "#fff",
        border: "1px dashed #ccc"
      }}
    >
      <h3 className="text-xl font-semibold mb-4" style={{ color: "#155231" }}>
        Benefits
      </h3>
      <p className="mb-4 text-sm" style={{ color: "#155231" }}>
        Vote on this poll and earn exclusive rewards if you guess correctly!
      </p>
      <ul className="space-y-3">
        <li className="flex items-center gap-2 font-medium" style={{ color: "#155231" }}>
          <Trophy className="h-5 w-5" />
          <span>Badges</span>
        </li>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <li
                className={`flex items-center gap-2 font-medium cursor-help ${activeBenefits.gameRewards ? "" : "cursor-help"}`}
                style={{ color: activeBenefits.gameRewards ? "#155231" : "rgba(21, 82, 49, 0.4)" }}
              >
                <Gamepad2 className="h-5 w-5" />
                <span>In-game rewards</span>
              </li>
            </TooltipTrigger>
            {!activeBenefits.gameRewards && (
              <TooltipContent>
                <p>Not active for this poll</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <li
                className={`flex items-center gap-2 font-medium ${activeBenefits.promoCodes ? "" : "cursor-help"}`}
                style={{ color: activeBenefits.promoCodes ? "#155231" : "rgba(21, 82, 49, 0.4)" }}
              >
                <Ticket className="h-5 w-5" />
                <span>Promo codes & discounts</span>
              </li>
            </TooltipTrigger>
            {!activeBenefits.promoCodes && (
              <TooltipContent>
                <p>Not active for this poll</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <li
                className={`flex items-center gap-2 font-medium ${activeBenefits.personalizedProducts ? "" : "cursor-help"}`}
                style={{ color: activeBenefits.personalizedProducts ? "#155231" : "rgba(21, 82, 49, 0.4)" }}
              >
                <ShoppingBag className="h-5 w-5" />
                <span>Personalized products & more!</span>
              </li>
            </TooltipTrigger>
            {!activeBenefits.personalizedProducts && (
              <TooltipContent>
                <p>Not active for this poll</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </ul>
    </Card>
  );
};

export default BenefitsCard;
